import type { ReactNode } from "react"
import { Navigate, useLocation, type Location } from "react-router-dom"
import { useEffect, useRef } from "react"
import { useAtomValue } from "jotai"
import {
  isAuthenticatedAtom,
  isInitializingAtom,
  requiresTwoFactorAtom,
  userJwtSyncAtom,
} from "./atoms"
import { useAuth } from "./useAuth"
import { hasPanelAccess } from "./scopes"
import StaffLoginPage from "@/pages/StaffLoginPage"
import { safeError } from "@/utils/safeError"

const LOGIN_PATHS = new Set(["/auth/login", "/auth/staff-login"])

interface ProtectedRouteProps {
  children: ReactNode
  requireAuth?: boolean
  autoRedirect?: boolean
}

/**
 * Protected Route component using the new auth system
 * Simple protection - just checks if user is authenticated
 */
const ProtectedRoute = ({
  children,
  requireAuth = true,
  autoRedirect = true,
}: ProtectedRouteProps) => {
  // Only subscribe to the specific atoms we need for routing decisions
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const isInitializing = useAtomValue(isInitializingAtom)
  const requiresTwoFactor = useAtomValue(requiresTwoFactorAtom)
  const userJwt = useAtomValue(userJwtSyncAtom)
  const location = useLocation()
  const { logout } = useAuth()
  const hasLoggedOutRef = useRef(false)

  // Panel gate. Anonymous (guest) sessions never get in, and neither does an
  // ordinary signed-in app user. Holding at least one scope is the whole test:
  // it covers admins, whole-panel viewers and area viewers alike, so this
  // component never has to learn what a role means.
  const isStaff =
    (isAuthenticated &&
      userJwt &&
      !userJwt.is_anon &&
      (userJwt.roles?.includes("staff") || hasPanelAccess(userJwt))) ||
    false

  // A real (non-anon) session that isn't staff shouldn't linger — log it out so
  // its stale token is replaced when the user signs in with a staff account.
  useEffect(() => {
    if (
      isAuthenticated &&
      userJwt &&
      !userJwt.is_anon &&
      !isStaff &&
      !hasLoggedOutRef.current &&
      !isInitializing
    ) {
      hasLoggedOutRef.current = true
      logout().catch((error) => {
        console.error("Failed to logout non-staff user:", safeError(error))
      })
    }
  }, [isAuthenticated, userJwt, isStaff, isInitializing, logout])

  // Show loading spinner while doing initial authentication check
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  // If user needs 2FA, send them to the verification page. (The page itself is
  // not wrapped in ProtectedRoute, so this never loops.)
  if (requiresTwoFactor) {
    return <Navigate to="/auth/2fa" state={{ from: location }} replace />
  }

  // Protected routes (the admin panel) show the staff magic-link login in place
  // for anyone who isn't a signed-in staff/admin — no redirect to the app-download
  // page. Any lingering non-staff session is being logged out by the effect above.
  if (requireAuth && !isStaff) {
    return <StaffLoginPage />
  }


  // A staff session on a login page has nothing to do there. This is where a
  // Cloudflare Access sign-in lands when Access returns to the last URL (say
  // the staff login after a logout): the exchange already signed them in
  // during initialisation, so send them on to the panel.
  if (
    !requireAuth &&
    isStaff &&
    autoRedirect &&
    LOGIN_PATHS.has(location.pathname)
  ) {
    const from = (location.state as { from?: Location } | null)?.from
    return <Navigate to={from?.pathname ?? "/admin"} replace />
  }

  return <>{children}</>
}

export default ProtectedRoute
